import { useBilling } from '@hook'
import { Button, Modal } from '@ui'
import { getError, showToast } from '@util'
import { useMemo, useState } from 'react'

type RecordPaymentProps = {
  amountPaid?: string | null
  grandTotal: number
  invoiceId: string
  onClose: () => void
  onSuccess: (status: string) => void
  status: string
}

function defaultAmount(status: string, grandTotal: number, amountPaid?: string | null): string {
  if (status === 'paid') return grandTotal > 0 ? grandTotal.toFixed(2) : '0'
  if (status === 'pending') return '0'
  if (amountPaid != null && amountPaid !== '') return String(amountPaid)
  return ''
}

function validateAmount(status: string, paid: number, grandTotal: number): string | null {
  if (!Number.isFinite(paid) || paid < 0) return 'Enter a valid amount paid'
  if (status === 'pending' && paid !== 0) return 'Pending status requires amount paid of 0'
  if (status === 'paid' && Math.abs(paid - grandTotal) > 0.009) {
    return 'Paid status requires amount paid to equal the total'
  }
  if (status === 'partially_paid') {
    if (paid <= 0 || paid >= grandTotal) {
      return 'Partially paid requires an amount greater than 0 and less than the total'
    }
  }
  return null
}

export const RecordPayment = ({
  amountPaid,
  grandTotal,
  invoiceId,
  onClose,
  onSuccess,
  status
}: RecordPaymentProps) => {
  const { updateInvoice } = useBilling({ id: invoiceId })
  const [paidInput, setPaidInput] = useState(() => defaultAmount(status, grandTotal, amountPaid))
  const [isSubmitting, setIsSubmitting] = useState(false)

  const paidAmount = Number.parseFloat(paidInput)
  const remaining = useMemo(() => {
    const paid = Number.isFinite(paidAmount) ? paidAmount : 0
    return Math.max(0, grandTotal - paid)
  }, [grandTotal, paidAmount])

  const handleSubmit = async () => {
    const paid = Number.parseFloat(paidInput)
    const error = validateAmount(status, paid, grandTotal)
    if (error) {
      showToast(error)
      return
    }

    const payload = {
      data: {
        id: invoiceId,
        type: 'invoice',
        attributes: {
          status,
          amount_paid: paid.toFixed(2)
        }
      }
    }

    setIsSubmitting(true)
    try {
      await updateInvoice({ formVal: payload, id: invoiceId })
      showToast('Payment recorded successfully')
      onSuccess(status)
      onClose()
    } catch (err) {
      getError(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const statusLabel = status === 'partially_paid' ? 'Partially paid' : status.charAt(0).toUpperCase() + status.slice(1)

  return (
    <Modal onClose={onClose} style={{ minWidth: 'min(480px, calc(100vw - 1.5rem))' }} title="Record payment">
      <div className="flex flex-col gap-5 px-6 pb-6 pt-2">
        <p className="text-sm text-[#555]">
          Setting status to <span className="font-medium text-[#111]">{statusLabel}</span>. Enter the amount paid toward
          this invoice.
        </p>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#444]">Amount paid</span>
          <div className="relative">
            <input
              className="w-full rounded-lg border border-[#D9D9D9] px-3 py-2.5 pr-14 text-sm"
              inputMode="decimal"
              onChange={e => setPaidInput(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="0.00"
              type="text"
              value={paidInput}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#666]">
              NGN
            </span>
          </div>
        </label>

        <div className="rounded-xl bg-[#F5F5F5] px-4 py-4">
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-[#555]">Invoice total</span>
              <span className="text-[#111]">NGN {grandTotal.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-[#555]">Amount paid</span>
              <span className="text-[#111]">NGN {(Number.isFinite(paidAmount) ? paidAmount : 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-[#E0E0E0] pt-2">
              <span className="font-semibold text-[#111]">Balance due</span>
              <span className="font-semibold text-[#111]">NGN {remaining.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button kinds="outline" label="Cancel" onClick={onClose} size="sm" type="button" />
          <Button disabled={isSubmitting} label="Confirm" onClick={() => void handleSubmit()} size="sm" type="button" />
        </div>
      </div>
    </Modal>
  )
}
